/*
 * © 2026 Snyk Limited
 *
 * Licensed under the Apache License, Version 2.0 (the 'License');
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an 'AS IS' BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UploadRevisionUnsealedError = exports.MultipartFieldNameMissingError = exports.UploadRevisionIdMismatchError = exports.TotalUploadRevisionSizeLimitExceededError = exports.TotalUploadRevisionFileCountLimitExceededError = exports.PopulateRequestLimitExceededError = exports.FilePathTooLongError = exports.FileCountLimitExceededError = exports.TotalFilesSizeLimitExceededError = exports.FileTooLargeError = exports.UploadRevisionSealedError = exports.UploadRevisionNotFoundError = void 0;
const types_1 = require("../types");
const problem_error_1 = require("../problem-error");
/**
 * @class
 * @name UploadRevisionNotFoundError
 * @description The upload revision was not found. The upload revision may have expired or does not exist. Provide a valid upload revision identifier.
 * @summary Upload revision not found
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UploadRevisionNotFoundError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Upload revision not found',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0001',
            status: 404,
            description: `The upload revision was not found. The upload revision may have expired or does not exist. Provide a valid upload revision identifier.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0001',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UploadRevisionNotFoundError = UploadRevisionNotFoundError;
/**
 * @class
 * @name UploadRevisionSealedError
 * @description The upload revision cannot be modified after it has been sealed. Create a new upload revision and retry the operation.
 * @summary Upload revision is sealed
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UploadRevisionSealedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Upload revision is sealed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0002',
            status: 400,
            description: `The upload revision cannot be modified after it has been sealed. Create a new upload revision and retry the operation.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0002',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UploadRevisionSealedError = UploadRevisionSealedError;
/**
 * @class
 * @name FileTooLargeError
 * @description One of the uploaded files exceeds the maximum allowed size. Reduce the file size and try again, or contact support for assistance.
 * @summary File too large
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FileTooLargeError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'File too large',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0003',
            status: 400,
            description: `One of the uploaded files exceeds the maximum allowed size. Reduce the file size and try again, or contact support for assistance.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0003',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FileTooLargeError = FileTooLargeError;
/**
 * @class
 * @name TotalFilesSizeLimitExceededError
 * @description The total size of all files uploaded in a single request exceeds the maximum allowed limit. Reduce the total size of files in the request.
 * @summary Total files size limit exceeded
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TotalFilesSizeLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Total files size limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0004',
            status: 400,
            description: `The total size of all files uploaded in a single request exceeds the maximum allowed limit. Reduce the total size of files in the request.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0004',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TotalFilesSizeLimitExceededError = TotalFilesSizeLimitExceededError;
/**
 * @class
 * @name FileCountLimitExceededError
 * @description The request includes more files than the maximum allowed file count. Reduce the number of files in the request.
 * @summary File count limit exceeded
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FileCountLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'File count limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0005',
            status: 400,
            description: `The request includes more files than the maximum allowed file count. Reduce the number of files in the request.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0005',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FileCountLimitExceededError = FileCountLimitExceededError;
/**
 * @class
 * @name FilePathTooLongError
 * @description One or more file paths exceed the maximum allowed length. Reduce the length of the file paths.
 * @summary File path too long
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class FilePathTooLongError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'File path too long',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0006',
            status: 400,
            description: `One or more file paths exceed the maximum allowed length. Reduce the length of the file paths.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0006',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.FilePathTooLongError = FilePathTooLongError;
/**
 * @class
 * @name PopulateRequestLimitExceededError
 * @description The number of populate requests for this upload revision exceeds the maximum allowed limit. Create a new upload revision and upload a revision using fewer requests.
 * @summary Populate request limit exceeded
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class PopulateRequestLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Populate request limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0007',
            status: 400,
            description: `The number of populate requests for this upload revision exceeds the maximum allowed limit. Create a new upload revision and upload a revision using fewer requests.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0007',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.PopulateRequestLimitExceededError = PopulateRequestLimitExceededError;
/**
 * @class
 * @name TotalUploadRevisionFileCountLimitExceededError
 * @description The number of files exceeds the limit for a single upload revision. Consider splitting the files across multiple upload revisions.
 * @summary Upload revision file count limit exceeded
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TotalUploadRevisionFileCountLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Upload revision file count limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0008',
            status: 400,
            description: `The number of files exceeds the limit for a single upload revision. Consider splitting the files across multiple upload revisions.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0008',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TotalUploadRevisionFileCountLimitExceededError = TotalUploadRevisionFileCountLimitExceededError;
/**
 * @class
 * @name TotalUploadRevisionSizeLimitExceededError
 * @description The total size of all files uploaded across the entire upload revision exceeds the maximum allowed limit. Consider splitting the files across multiple upload revisions.
 * @summary Upload revision size limit exceeded
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class TotalUploadRevisionSizeLimitExceededError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Upload revision size limit exceeded',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0009',
            status: 400,
            description: `The total size of all files uploaded across the entire upload revision exceeds the maximum allowed limit. Consider splitting the files across multiple upload revisions.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0009',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.TotalUploadRevisionSizeLimitExceededError = TotalUploadRevisionSizeLimitExceededError;
/**
 * @class
 * @name UploadRevisionIdMismatchError
 * @description The upload revision identifier in the request body does not match the upload revision identifier in the request path. Ensure both identifiers match and retry the request.
 * @summary Upload revision identifier mismatch
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UploadRevisionIdMismatchError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Upload revision identifier mismatch',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0010',
            status: 400,
            description: `The upload revision identifier in the request body does not match the upload revision identifier in the request path. Ensure both identifiers match and retry the request.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0010',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UploadRevisionIdMismatchError = UploadRevisionIdMismatchError;
/**
 * @class
 * @name MultipartFieldNameMissingError
 * @description The multipart request has a missing or empty name parameter in the Content-Disposition header. Ensure each part includes a valid field name.
 * @summary Missing field name in multipart request
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class MultipartFieldNameMissingError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Missing field name in multipart request',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0011',
            status: 400,
            description: `The multipart request has a missing or empty name parameter in the Content-Disposition header. Ensure each part includes a valid field name.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0011',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.MultipartFieldNameMissingError = MultipartFieldNameMissingError;
/**
 * @class
 * @name UploadRevisionUnsealedError
 * @description The upload revision cannot be consumed before it is sealed. Seal the revision or create a new one and retry the operation.
 * @summary Upload revision is unsealed
 * @category UploadRevision
 * @param {string} details                         the specific details that causes this error
 * @param {string} description                     the general description for this error
 * @param {Classification} classification          the error classification determining if the error is user-actionable or not
 * @param {Record<string, any>} [additionalData]   a collection of relevant data specific to this error
 * @param {Error} [cause]                          the `Error` type that caused this error to be thrown
 * @param {string} [instance]                      the instance to use for this error. Overrides 'instance' metadata
 */
class UploadRevisionUnsealedError extends problem_error_1.ProblemError {
    constructor(details, additionalData, cause, instance, logs) {
        super({
            title: 'Upload revision is unsealed',
            type: 'https://docs.snyk.io/scan-with-snyk/error-catalog#snyk-upload-revision-0012',
            status: 400,
            description: `The upload revision cannot be consumed before it is sealed. Seal the revision or create a new one and retry the operation.`,
            errorCode: 'SNYK-UPLOAD-REVISION-0012',
            level: 'error',
            classification: types_1.Classification.ACTIONABLE,
            instance,
        }, details, Object.assign({ links: [] }, additionalData), cause, logs);
        this.name = this.constructor.name;
    }
}
exports.UploadRevisionUnsealedError = UploadRevisionUnsealedError;
//# sourceMappingURL=UploadRevision-error-catalog.js.map